import React, { useState } from "react";
import { AlertTriangle, Loader } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const DeleteAccountSection = () => {
  const { deleteAccount } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const handleDeleteClick = () => {
    setShowModal(true);
    setError("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError("");
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    setError("");

    try {
      const result = await deleteAccount();

      if (!result.success) {
        setError(result.error || "Failed to delete account");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white p-4">
        <h3 className="font-semibold text-black mb-4">Delete Account</h3>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-medium text-black">Delete My Account</p>
              <p className="text-xs text-gray-600">
                Permanently remove your account and all data
              </p>
            </div>
          </div>
          <button
            onClick={handleDeleteClick}
            className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 font-semibold text-sm transition-colors"
          >
            Delete
          </button>
        </div>

        <div className="bg-white rounded-lg p-3 border border-gray-400">
          <p className="text-sm text-gray-600 leading-relaxed">
            <strong>Deleting your account is permanent and cannot be undone.</strong> When you delete your account, your profile, posts, collections, comments, likes, and all other data will be permanently removed.
            Your username will become available for others, and you will be logged out from all devices.
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-lg">
            {/* Modal Header */}
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-black">
                Are you sure you want to delete your account?
              </h3>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 mb-2 leading-relaxed">
                If you delete your account, your profile, posts, collections, comments, likes, and all other data will be permanently removed. You will not be able to recover your account.
              </p>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg mb-4">
                  {error}
                </div>
              )}
            </div>

            {/* Modal Footer - Buttons */}
            <div className="border-t border-gray-150 flex flex-col">
              <button
                onClick={handleConfirmDelete}
                disabled={isLoading}
                className="w-full px-4 py-3 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>

              <button
                onClick={handleCloseModal}
                disabled={isLoading}
                className="w-full px-4 py-3 border-t border-gray-150 text-black disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountSection;
